import { useEffect, useMemo, useRef } from "react";
import { useAppStore } from "../../state/app-store";
import { useExternalDataStore } from "../../state/external-data-store";
import { joinPath } from "../../utils/class-utils";
import {
  buildLatestItemsSharedClasses,
  LatestItemsEditorNote,
  LatestItemsErrorState,
  LatestItemsLoadingState,
  LatestItemsSectionFrame,
} from "./shared/latest-items-ui";
import { TableInteractionNote } from "./shared/TableInteractionNote";
import { withReturnContext } from "./shared/link-context";
import { useEditorSafeAnchorClick } from "./shared/use-editor-safe-anchor-click";
import { CardItemIdRoot, RowItemIdRoot } from "./shared/item-id-comment";
import type { NewsLatestRowProps, ResolvedNewsItem } from "./types";

export function NewsLatestRow(props: NewsLatestRowProps) {
  const {
    viewMode = "cards",
    basePath = "/news",
    bgClass = "no-class",
    paddingYClass = "py-section-md",
    cardBgClass = "no-class",
    cardBorderClass = "no-class",
    cardBorderColorClass = "no-class",
    contentAlignClass = "no-class",
    buttonColorClass = "primary",
    itemLimit = "3",
  } = props;

  // Generate stable instance ID for this component
  const instanceIdRef = useRef<string>(`news-latest-${Math.random().toString(36).slice(2, 11)}`);
  const instanceId = instanceIdRef.current;

  const newsLatestState = useExternalDataStore((state) => state.newsLatest[instanceId]);
  const ensureNewsLatest = useExternalDataStore((state) => state.ensureNewsLatest);
  const loadLatestNews = useExternalDataStore((state) => state.loadLatestNews);
  const resetNewsLatest = useExternalDataStore((state) => state.resetNewsLatest);
  const { isEditor, handleAnchorClick } = useEditorSafeAnchorClick();
  const pageId = useAppStore((state) => state.documentId);

  const items = (newsLatestState?.items ?? []) as ResolvedNewsItem[];
  const isLoading = newsLatestState?.isLoading ?? true;
  const errorText = newsLatestState?.error ?? null;

  const classes = {
    ...buildLatestItemsSharedClasses({
      sectionClassName: "section-news-latest",
      bgClass,
      paddingYClass,
      cardBgClass,
      cardBorderClass,
      cardBorderColorClass,
      contentAlignClass,
      buttonColorClass,
    }),
    date: "small fw-normal",
  };

  const stableBasePath = useMemo(() => basePath, [basePath]);

  useEffect(() => {
    ensureNewsLatest(instanceId);
    const limit = itemLimit === "all" ? 999 : parseInt(itemLimit, 10);
    void loadLatestNews(instanceId, limit);
    return () => resetNewsLatest(instanceId);
  }, [ensureNewsLatest, instanceId, itemLimit, loadLatestNews, resetNewsLatest]);

  if (isLoading) {
    return <LatestItemsLoadingState classes={classes} message="Loading latest news…" />;
  }

  if (errorText) {
    return <LatestItemsErrorState classes={classes} errorText={errorText} />;
  }

  // In live view, hide the entire section if there's no data
  if (!items.length && !isEditor) {
    return null;
  }

  const renderNoDataNote = () => (
    <LatestItemsEditorNote
      classes={classes}
      message="No news items available at this time. This section will not appear in the live view until news content is available."
    />
  );

  const renderCards = () => (
    <div className={classes.row}>
      {items.map((n, idx) => {
        const title = n.item_name ?? "Untitled";
        const teaser = n.teaser ?? "";
        const href = n.slug ? withReturnContext(joinPath(stableBasePath, n.slug), pageId) : undefined;

        return (
          <div className={classes.col} key={n.__id ?? idx}>
            <CardItemIdRoot itemId={n.item_id} className={classes.card}>
              <div className={classes.cardBody}>
                <div className={classes.header}>
                  <div>
                    <h3 className={classes.title}>{title}</h3>
                    {n.date_posted ? (
                      <div className={classes.date}>Date Posted: {n.date_posted}</div>
                    ) : null}
                  </div>
                  {teaser ? (
                    <div
                      className={classes.teaser}
                      dangerouslySetInnerHTML={{ __html: teaser }}
                    />
                  ) : null}
                </div>

                <div className={classes.cta}>
                  {href ? (
                    <a
                      className={classes.ctaLink}
                      href={href}
                      onClick={handleAnchorClick}
                    >
                      Read more
                    </a>
                  ) : (
                    <button className={classes.ctaDisabled} type="button" disabled>
                      Missing slug
                    </button>
                  )}
                </div>
              </div>
            </CardItemIdRoot>
          </div>
        );
      })}
    </div>
  );

  const renderTable = () => (
    <div className={classes.tableWrap}>
      <div className="table-scroll">
        <table className={classes.table}>
        <thead className="text-bg-white sticky-top shadow-sm">
          <tr>
            <th scope="col">Title</th>
            <th scope="col">Date Posted</th>
            <th scope="col">Teaser</th>
            <th scope="col">Action</th>
          </tr>
        </thead>
        <tbody>
          {items.map((n, idx) => {
            const title = n.item_name ?? "Untitled";
            const teaser = n.teaser ?? "";
            const href = n.slug ? withReturnContext(joinPath(stableBasePath, n.slug), pageId) : undefined;

            return (
              <RowItemIdRoot itemId={n.item_id} key={n.__id ?? idx}>
                <td>
                  <span className={classes.tableTitle}>{title}</span>
                </td>
                <td className="text-nowrap">
                  {n.date_posted || <span>—</span>}
                </td>
                <td>
                  {teaser ? (
                    <div dangerouslySetInnerHTML={{ __html: teaser }} />
                  ) : (
                    <span>—</span>
                  )}
                </td>
                <td className="text-nowrap">
                  {href ? (
                    <a
                      className={classes.tableCta}
                      href={href}
                      onClick={handleAnchorClick}
                    >
                      Read more
                    </a>
                  ) : (
                    <button className={classes.tableCtaDisabled} type="button" disabled>
                      Missing slug
                    </button>
                  )}
                </td>
              </RowItemIdRoot>
            );
          })}
        </tbody>
        </table>
      </div>
      <TableInteractionNote />
    </div>
  );

  return (
    <LatestItemsSectionFrame classes={classes} title="Latest News">
      {items.length === 0 ? renderNoDataNote() : viewMode === "table" ? renderTable() : renderCards()}
    </LatestItemsSectionFrame>
  );
}


